import { useState } from 'react'
import { BillingCard, Button, Card, CardSubtitle, CardTitle, LanguageCard, track, useSession } from '@quoter/web-core'
import { useI18n } from '@/hooks/useI18n'
import { useMe } from '@/hooks/useMe'
import { useDeleteMyData } from '@/hooks/useJourney'
import { legalUrls } from '@/lib/product'
import { db } from '@/lib/db'
import { today } from '@/lib/dates'

/** Language, billing, and a way to take your data with you. No team card: a LowFodmaps account is one person. */
export function Settings() {
  const { t } = useI18n()
  const session = useSession()
  const isAdmin = session.status === 'authenticated' && session.isAdmin

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <h1 className="font-display text-[34px] font-normal tracking-tightish text-gray-900">{t.settings.title}</h1>
      </header>

      <LanguageCard />
      {isAdmin && <BillingCard />}
      <ExportCard />
      <DeleteCard />
      <LegalLinks />
    </div>
  )
}

/** Erase everything logged. Also withdraws the health-data consent, so the consent screen returns. */
function DeleteCard() {
  const { t } = useI18n()
  const remove = useDeleteMyData()
  return (
    <Card>
      <CardTitle>{t.settings.deleteTitle}</CardTitle>
      <CardSubtitle>{t.settings.deleteHint}</CardSubtitle>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button
          variant="danger"
          disabled={remove.isPending}
          onClick={() => {
            if (window.confirm(t.settings.deleteConfirm)) remove.mutate()
          }}
        >
          {remove.isPending ? t.settings.deleting : t.settings.delete}
        </Button>
        {remove.isSuccess && <span className="text-[12.5px] text-green-700">{t.settings.deleted}</span>}
        {remove.isError && (
          <span className="text-[12.5px] text-red-700" role="alert">
            {t.common.error}
          </span>
        )}
      </div>
      <p className="mt-4 text-[12.5px] leading-relaxed text-gray-500">{t.settings.deleteAccount}</p>
    </Card>
  )
}

function LegalLinks() {
  const { t, locale } = useI18n()
  const urls = legalUrls(locale)
  const link = 'underline underline-offset-2 hover:text-gray-900'
  return (
    <p className="text-[12.5px] leading-relaxed text-gray-500">
      {t.disclaimer}{' '}
      <a href={urls.privacy} target="_blank" rel="noopener" className={link}>
        {t.legal.privacy}
      </a>{' '}
      ·{' '}
      <a href={urls.terms} target="_blank" rel="noopener" className={link}>
        {t.legal.terms}
      </a>
    </p>
  )
}

/** Everything the person logged, as one JSON file, built in the browser from their own rows. */
function ExportCard() {
  const { t } = useI18n()
  const me = useMe()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  async function download() {
    if (!me || busy) return
    setBusy(true)
    setFailed(false)
    // Owner clauses on every read, as everywhere else; RLS enforces the same pair.
    const { orgId, userId } = me
    const [journey, diary, symptoms, challenges] = await Promise.all([
      db.from('journeys').select('phase, elimination_started_on').eq('organization_id', orgId).eq('user_id', userId),
      db
        .from('diary_entries')
        .select('entry_date, meal, body, created_at')
        .eq('organization_id', orgId)
        .eq('user_id', userId)
        .order('entry_date'),
      db
        .from('symptom_logs')
        .select('log_date, score, note')
        .eq('organization_id', orgId)
        .eq('user_id', userId)
        .order('log_date'),
      db
        .from('challenges')
        .select('fodmap_group, status, started_on, days')
        .eq('organization_id', orgId)
        .eq('user_id', userId),
    ])
    setBusy(false)
    if (journey.error || diary.error || symptoms.error || challenges.error) {
      setFailed(true)
      return
    }
    const file = new Blob(
      [
        JSON.stringify(
          {
            exported_on: today(),
            journey: journey.data?.[0] ?? null,
            diary: diary.data,
            symptoms: symptoms.data,
            challenges: challenges.data,
          },
          null,
          2,
        ),
      ],
      { type: 'application/json' },
    )
    const url = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = url
    link.download = `lowfodmaps-${today()}.json`
    link.click()
    URL.revokeObjectURL(url)
    track('data_exported')
  }

  return (
    <Card>
      <CardTitle>{t.settings.dataTitle}</CardTitle>
      <CardSubtitle>{t.settings.dataHint}</CardSubtitle>
      <div className="mt-5 flex items-center gap-3">
        <Button variant="secondary" onClick={() => void download()} disabled={busy}>
          {busy ? t.settings.exporting : t.settings.export}
        </Button>
        {failed && (
          <span className="text-[12.5px] text-red-700" role="alert">
            {t.settings.exportError}
          </span>
        )}
      </div>
    </Card>
  )
}
